import React, { useState } from 'react';

import classNames from 'classnames';
import TrashIcon from 'icons/ic-trash.svg';
import PersonIcon from 'icons/person.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useLatestVersionQuery } from 'components/hooks/latestVersionQueries';
import { useProjectPersonnelsQuery } from 'components/hooks/projectPersonnelQueries';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import RowBar from 'components/application/RowBar';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';
import TrainingDownloadOptions from 'components/training/TrainingDownloadOptions';

export default function ProjectPersonnelBar({
  onAssignPersonnelClick,
  onUnassignPersonnelClick,
  selectedProjectPersonnelIds,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
    isAllowedFeature,
  } = useCurrentActor();

  const { personnelPolicy, projectId, projectPersonnelPolicy } =
    useProjectPersonnelContext();

  const [isTrainingEvidenceProcessed, setIsTrainingEvidenceProcessed] =
    useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectPersonnel.components.ProjectPersonnelBar',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { data: domainLatestVersion, isSuccess: isDomainLatestVersionSuccess } =
    useLatestVersionQuery({ projectId });

  const {
    data: domainProjectPersonnel,
    isSuccess: isDomainProjectPersonnelSuccess,
  } = useProjectPersonnelsQuery({ params: { meta_only: true }, projectId });

  const isTrainingButtonVisible =
    personnelPolicy.canRead && isAllowedFeature('training_register');

  if (!(isDomainLatestVersionSuccess && isDomainProjectPersonnelSuccess)) {
    return <Skeleton isTrainingButtonVisible={isTrainingButtonVisible} />;
  }

  const { id: latestVersionId } = domainLatestVersion.data;

  const { scopedCount } = domainProjectPersonnel.meta;

  const disabledTooltip =
    scopedCount === 0 ?
      i18nTCommon('shared.noRecordsToDownload')
    : !isTrainingEvidenceProcessed &&
      i18nTCommon('shared.currentlyUploadingEvidence');

  const isDisabled = !!disabledTooltip;

  const selectedCount = selectedProjectPersonnelIds.size;
  const isSelecting = projectPersonnelPolicy.canDestroy && selectedCount > 0;

  return (
    <RowBar
      actions={
        <div className='tw-flex tw-items-center tw-gap-2.5'>
          {projectPersonnelPolicy.canCreate && scopedCount > 0 && (
            <OutlinedButton
              color='grey'
              onClick={onAssignPersonnelClick}
              size='sm'
            >
              <div className='tw-flex tw-items-center tw-gap-1'>
                <PersonIcon className='-tw-ml-2' height={24} width={24} />
                {i18nTCommon('personnel.addAssignPersonnel')}
              </div>
            </OutlinedButton>
          )}
          {isTrainingButtonVisible && (
            <div className='tooltip-parent'>
              <TrainingDownloadOptions
                disabled={isDisabled}
                documentGeneratorParams={{}}
                documentGeneratorUrl={`/projects/${projectId}/training_report_pdfs`}
                emailReportUrl={`/versions/${latestVersionId}/messages/new?training_report_included=true`}
                evidenceStatusUrl={`/projects/${projectId}/training_evidence_status`}
                setTrainingEvidenceProcessed={setIsTrainingEvidenceProcessed}
                trainingEvidenceProcessed={isTrainingEvidenceProcessed}
              />
              {isDisabled && (
                <Tooltip
                  placement='top'
                  tooltip={disabledTooltip}
                  trigger='hover'
                />
              )}
            </div>
          )}
        </div>
      }
      content={
        <div className='tw-flex tw-items-center tw-gap-6'>
          <div className='tw-font-inter tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-700'>
            {isSelecting ?
              i18nT('selectedCount', { count: selectedCount })
            : i18nT('totalCount', { count: scopedCount })}
          </div>
          {isSelecting && (
            <button
              className='tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-4 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100'
              onClick={onUnassignPersonnelClick}
              type='button'
            >
              <TrashIcon
                className='[&_path]:tw-fill-grey-700'
                height={20}
                width={20}
              />
              {i18nTCommon('projects.unassignFromProject', {
                interpolation: { escapeValue: false },
                singularRAMSName,
              })}
            </button>
          )}
        </div>
      }
      modifiers={['border-bottom-none', 'enlarged']}
    />
  );
}

ProjectPersonnelBar.propTypes = {
  onAssignPersonnelClick: PropTypes.func.isRequired,
  onUnassignPersonnelClick: PropTypes.func.isRequired,
  selectedProjectPersonnelIds: PropTypes.instanceOf(Set).isRequired,
};

function Skeleton({ isTrainingButtonVisible }) {
  return (
    <RowBar
      actions={
        <div
          className={classNames(
            'tw-h-8 tw-animate-pulse tw-rounded-lg tw-bg-grey-100',
            isTrainingButtonVisible ? 'tw-w-80' : 'tw-w-36',
          )}
        />
      }
      content={
        <div className='tw-h-6 tw-w-35 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
      }
      modifiers={['border-bottom-none', 'enlarged']}
    />
  );
}

Skeleton.propTypes = {
  isTrainingButtonVisible: PropTypes.bool.isRequired,
};
